import { takeLatest, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';
import {
  AboutTypes,
  Mutations,
  Requests,
  ResourceRequest,
  Resources,
} from '@erecruit/types';
import {
  getResourceApi,
  MatchSearchRequestWithDataPref,
  MatchSearchCountRequestWithDataPref,
} from '@erecruit/modules';

import {
  approveApprovalPathFail,
  approveApprovalPathSuccess,
  closePositionRecordFail,
  closePositionRecordSuccess,
  fetchMatchStatusFail,
  fetchMatchStatusSuccess,
  fetchPositionRecord,
  fetchPositionRecordFail,
  fetchPositionRecordInfo,
  fetchPositionRecordInfoFail,
  fetchPositionRecordInfoSuccess,
  fetchPositionRecordSuccess,
  populateEditPositionModalSuccess,
  PositionRecordState,
  rejectApprovalPathFail,
  rejectApprovalPathSuccess,
  savePositionRecordFail,
  selectors,
} from './reducer';
import { PositionField } from '../../AddNewPositionWizard/types';
import {
  getSelectFields,
  getDataSourceForField,
} from '../../RequestPosition/state/helpers';
import {
  MutationType,
  PositionRecordMatchCounters,
  PositionRecordResponse,
  PositionRecordInfoResponse,
  PositionRecordSections,
  PositionInfoSections,
  PositionRecordMutation,
  FormSpec,
} from '../types';
import { MATCH_STATUS_TYPES_GROUPS } from '../helpers';
import { withStatusInfo } from '../../ActionStatus';

const getInfoSections = (
  data: PositionRecordInfoResponse,
): PositionInfoSections => {
  const [positionGeneral] = data;

  return {
    aboutPosition: positionGeneral.data[0],
  };
};

const getSections = (
  data: PositionRecordResponse,
  infoSections: PositionInfoSections,
): PositionRecordSections => {
  const [
    positionLocation,
    positionSchedule,
    positionBilling,
    ,
    ,
    ,
    ,
    ,
    positionApprovalPath,
    positionApprovalRejectionReasons,
    positionAdditionalInfo,
  ] = data;

  return {
    ...infoSections,
    location: positionLocation.data[0],
    schedule: positionSchedule.data[0],
    billing: positionBilling.data[0],
    approvalPath: positionApprovalPath.data[0],
    approvalRejectionReasons: positionApprovalRejectionReasons.data,
    additionalInfo: positionAdditionalInfo.data,
  };
};

const getCounters = (
  data: PositionRecordResponse,
): PositionRecordMatchCounters => {
  const [
    ,
    ,
    ,
    submittals,
    interview,
    offers,
    candidateAccepted,
    fullPlacement,
  ] = data;

  return {
    submittals: submittals.data[0].value,
    interview: interview.data[0].value,
    offers: offers.data[0].value,
    candidateAccepted: candidateAccepted.data[0].value,
    fullPlacement: fullPlacement.data[0].value,
  };
};

function* processFetchPositionRecordInfo({
  payload: id,
}: PayloadAction<number>) {
  try {
    const filter: Resources.PositionSearchFilter = {
      ids: [id],
    };
    const data: PositionRecordInfoResponse = yield getResourceApi().getResources(
      [new Requests.PositionRequest({ filter })],
    );

    yield put(
      fetchPositionRecordInfoSuccess({
        infoSections: getInfoSections(data),
      }),
    );
  } catch (e) {
    yield put(fetchPositionRecordInfoFail(e.message));
  }
}

function* processFetchPositionRecord({ payload: id }: PayloadAction<number>) {
  try {
    const positionRecord: PositionRecordState = yield select(
      selectors.selectPositionRecordState,
    );
    const infoSections = positionRecord.infoSections;

    if (!infoSections) {
      yield put(fetchPositionRecordInfoFail('PositionInfo missing'));
      return;
    }

    const filter: Resources.PositionSearchFilter = {
      ids: [id],
    };
    const data: PositionRecordResponse = yield getResourceApi().getResources([
      new Requests.PositionLocationRequest({ filter }),
      new Requests.PositionScheduleRequest({ filter }),
      new Requests.PositionBillingRequest({ filter }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchStatusTypes: MATCH_STATUS_TYPES_GROUPS.submittals,
          positionIds: [id],
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchStatusTypes: MATCH_STATUS_TYPES_GROUPS.interview,
          positionIds: [id],
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchStatusTypes: MATCH_STATUS_TYPES_GROUPS.offers,
          positionIds: [id],
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchStatusTypes: MATCH_STATUS_TYPES_GROUPS.candidateAccepted,
          positionIds: [id],
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchStatusTypes: MATCH_STATUS_TYPES_GROUPS.fullPlacement,
          positionIds: [id],
        },
      }),
      new Requests.ApprovalPathRequest({
        aboutType: AboutTypes.Position,
        referenceId: id,
      }),
      new Requests.ApprovalRejectionReasonRequest({}),
      new Requests.CustomFieldRequest({
        filter: {
          aboutObjects: [{ aboutType: AboutTypes.Position, referenceId: id }],
        },
      }),
    ]);

    yield put(
      fetchPositionRecordSuccess({
        sections: getSections(data, infoSections),
        counters: getCounters(data),
      }),
    );
  } catch (e) {
    yield put(fetchPositionRecordFail(e.message));
  }
}

function* closePositionRecordErrorHandler(e: Error) {
  yield put(closePositionRecordFail(e.message));
}

function* closePositionRecord({
  payload: { positionId },
}: PayloadAction<{ positionId: number; positionName: string }>) {
  const response = yield getResourceApi().createMutationRequest(
    new Requests.ClosePositionRequest({ positionId }),
  );

  yield put(closePositionRecordSuccess(response));
  yield put(fetchPositionRecordInfo(positionId));
  yield put(fetchPositionRecord(positionId));
}

function* savePositionRecord({
  payload,
}: PayloadAction<PositionRecordMutation>) {
  try {
    const mutationType = MutationType[payload.section];
    const RecordPageState = yield select(selectors.selectPositionRecordState);
    let id = RecordPageState.sections[payload.section].id;

    if (payload.section === 'billing') {
      id = RecordPageState.sections['aboutPosition'].id;
      payload.positionValues.billRate = payload.positionValues.targetBillRate;
    }

    if (payload.section === 'schedule') {
      payload.positionValues.shiftTypes = payload.positionValues.shiftTypeId
        ? [Number(payload.positionValues.shiftTypeId)]
        : [];
    }

    if (payload.section === 'location') {
      payload.positionValues.facilityDepartments =
        payload.positionValues.facilityDepartments || [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let params: any = {
      id,
      positionId: id,
      ...payload.positionValues,
    };

    if (payload.section === 'additionalInfo') {
      const customFields: Mutations.CustomField[] = Object.keys(
        payload.positionValues,
      ).map(key => ({
        id: Number(key),
        value: JSON.stringify(payload.positionValues[key]),
      }));

      id = RecordPageState.sections['aboutPosition'].id;
      params = {
        customFields: customFields,
        aboutType: AboutTypes.Position,
        referenceId: id,
      };
    }

    yield getResourceApi().createMutationRequest({
      mutationType: mutationType,
      mutationParams: params,
    });

    yield put(fetchPositionRecord(id));
  } catch (e) {
    yield put(savePositionRecordFail(e.message));
  }
}

function* populateEditPositionModal({ payload }: PayloadAction<FormSpec>) {
  // TODO: similar code as we did in fetchPositionTemplateDetails saga. In future we need to refactor this
  const selectFields = getSelectFields([...payload.fields]);
  const fieldsWithData: PositionField[] = [...payload.fields];
  const resources: ResourceRequest[] = [];
  selectFields.forEach(field => {
    const dataSource = getDataSourceForField(field);
    if (dataSource) {
      resources.push(dataSource);
    }
  });

  const responses = yield getResourceApi().getResources(resources);

  selectFields.forEach((field, index) => {
    const data = responses[index].data;
    const templateFieldIndex = payload.fields.findIndex(
      (f: PositionField) => f.fieldType === field.fieldType,
    );

    if (templateFieldIndex !== -1) {
      const field = {
        ...payload.fields[templateFieldIndex],
      };

      field.data = data;
      fieldsWithData[templateFieldIndex] = field;
    }
  });

  yield put(populateEditPositionModalSuccess(fieldsWithData));
}

function* watchForClosePositionRecord() {
  yield takeLatest(
    'positionRecord/closePositionRecord',
    withStatusInfo({
      successGeneratorFn: closePositionRecord,
      errorGeneratorFn: closePositionRecordErrorHandler,
      autocloseMs: 2000,
      getSuccessMessage: ({ payload: { positionName } }) => ({
        id: messageIds.vms.positionRecord.closePosition.successInfo,
        values: { position: positionName },
      }),
      getLoadingMessage: ({ payload: { positionName } }) => ({
        id: messageIds.vms.positionRecord.closePosition.progressInfo,
        values: { position: positionName },
      }),
      getHeader: ({ payload: { positionName } }) => ({
        title: {
          id: messageIds.vms.positionRecord.closePosition.actionTitle,
          values: { position: positionName },
        },
      }),
    }),
  );
}

function* fetchMatchStatus({
  payload,
}: PayloadAction<Resources.MatchSearchParams>) {
  try {
    const result = yield getResourceApi().getResources([
      new MatchSearchRequestWithDataPref(payload),
      new MatchSearchCountRequestWithDataPref({
        filter: payload.filter,
      }),
    ]);

    yield put(
      fetchMatchStatusSuccess({
        data: result[0].data,
        count: result[1].data[0].value,
      }),
    );
  } catch (e) {
    yield put(fetchMatchStatusFail(e));
  }
}

function* watchForFetchFullPlacement() {
  yield takeLatest('positionRecord/fetchMatchStatus', fetchMatchStatus);
}

function* watchForFetchPositionRecord() {
  yield takeLatest(
    'positionRecord/fetchPositionRecord',
    processFetchPositionRecord,
  );
}

function* watchForFetchPositionRecordInfo() {
  yield takeLatest(
    'positionRecord/fetchPositionRecordInfo',
    processFetchPositionRecordInfo,
  );
}

function* watchForPopulateEditPositionModal() {
  yield takeEvery(
    'positionRecord/populateEditPositionModal',
    populateEditPositionModal,
  );
}

function* watchForSavePositionRecord() {
  yield takeLatest('positionRecord/savePositionRecord', savePositionRecord);
}

function* approveApprovalPath({
  payload: { approvalPathId, note },
}: PayloadAction<Mutations.ApproveApprovalPathParams>) {
  yield getResourceApi().createMutationRequest(
    new Requests.ApproveApprovalPathRequest({ approvalPathId, note }),
  );
  yield put(approveApprovalPathSuccess());

  const positionRecord: PositionRecordState = yield select(
    selectors.selectPositionRecordState,
  );

  yield put(
    fetchPositionRecord(
      positionRecord.infoSections?.aboutPosition?.id as number,
    ),
  );
}

function* approveApprovalPathErrorHandler(e: Error) {
  yield put(approveApprovalPathFail(e.message));
}

function* watchForApproveApprovalPath() {
  yield takeLatest(
    'positionRecord/approveApprovalPath',
    withStatusInfo({
      successGeneratorFn: approveApprovalPath,
      errorGeneratorFn: approveApprovalPathErrorHandler,
      getHeader: () => ({
        title: { id: messageIds.vms.approvalPath.approve },
      }),
      getSuccessMessage: () => ({
        id: messageIds.vms.approvalPath.stepApproved,
      }),
      getLoadingMessage: () => ({
        id: messageIds.vms.approvalPath.loadingMessage,
      }),
      autocloseMs: 1500,
    }),
  );
}

function* rejectApprovalPath({
  payload: { approvalPathId, note, rejectionReasonId },
}: PayloadAction<Mutations.RejectApprovalPathParams>) {
  yield getResourceApi().createMutationRequest(
    new Requests.RejectApprovalPathRequest({
      approvalPathId,
      note,
      rejectionReasonId,
    }),
  );
  yield put(rejectApprovalPathSuccess());

  const positionRecord: PositionRecordState = yield select(
    selectors.selectPositionRecordState,
  );

  yield put(
    fetchPositionRecord(
      positionRecord.infoSections?.aboutPosition?.id as number,
    ),
  );
}

function* rejectApprovalPathErrorHandler(e: Error) {
  yield put(rejectApprovalPathFail(e.message));
}

function* watchForRejectApprovalPath() {
  yield takeLatest(
    'positionRecord/rejectApprovalPath',
    withStatusInfo({
      successGeneratorFn: rejectApprovalPath,
      errorGeneratorFn: rejectApprovalPathErrorHandler,
      getHeader: () => ({
        title: { id: messageIds.vms.approvalPath.reject },
      }),
      getSuccessMessage: () => ({
        id: messageIds.vms.approvalPath.stepRejected,
      }),
      getLoadingMessage: () => ({
        id: messageIds.vms.approvalPath.loadingMessage,
      }),
      autocloseMs: 1500,
    }),
  );
}

export default [
  watchForClosePositionRecord(),
  watchForFetchPositionRecordInfo(),
  watchForFetchFullPlacement(),
  watchForFetchPositionRecord(),
  watchForPopulateEditPositionModal(),
  watchForSavePositionRecord(),
  watchForApproveApprovalPath(),
  watchForRejectApprovalPath(),
];
